import { Box, Button, Paper, Stack, styled, Typography, useTheme } from '@mui/material';
import { GetRowIdParams, GridApi, SelectionChangedEvent } from 'ag-grid-community';
import { upperFirst } from 'lodash-es';
import { FC, memo, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';

import { AgTable } from '../../../../components/AgTable/AgTable';
import { ExportGridButton } from '../../../../components/AgTable/ExportGridButton';
import { useExtendedExportSettings } from '../../../../components/AgTable/exportToExcelDefs';
import { ActionButtonStack, ActionButtonWrapper } from '../../../../components/AgTable/gridActionBar';
import { multiRowSelectionDefs } from '../../../../components/AgTable/rowSelectionDefs';
import { MUIAvatar } from '../../../../components/Avatar/MUIAvatar';
import { FadeInGridWrapper } from '../../../../components/grid-renderers/FadeInGridWrapper';
import { useLoadingBarState } from '../../../../components/LoadingBar/LoadingBarContext';
import { ICompanyDataModel } from '../../../../data-models/company.data-model';
import { IKPIRequestDataModel, KPIRequestStatus } from '../../../../data-models/kpi-requests.data-model';
import {
  filterKPIRequestsByStatusesJ,
  kpiRequestErrorModalOpenState,
  kpiRequestsNotSentState,
} from '../../../../services/state/KPI/KPIRequestsStateJ';
import { portcosByIdMapAtomJ } from '../../../../services/state/PortCosStateJ';
import { FMT, FormatterService } from '../../../../util/formatter-service';
import { ExportButtonContainer } from './components/KpiStyleDefs';
import { useKPIRequestsNotSentActions } from './hooks/useKPIRequestsNotSentActions';
import useKPIRequestsNotSentColDefs from './hooks/useKPIRequestsNotSentColDefs';
import { RequestConfirmDialog } from './KPIRequestResponse/RequestConfirmDialog';
import { RequestFailDialog } from './KPIRequestResponse/RequestFailDialog';

const CompanyPaperContainer = styled(Paper)`
  display: grid;
  justify-content: space-between;
  align-items: center;
  grid-template-columns: repeat(4, 1fr);
  width: 100%;
  padding: 1rem 1.5rem;
  background-color: transparent;
`;

const CellContainer = styled('div')`
  align-items: center;
  column-gap: 0.75rem;
  display: grid;
  grid-template-columns: min-content 1fr;
  height: 100%;
`;

const RowTypography: FC<PropsWithChildren> = ({ children }) => {
  const { colors } = useTheme();

  return (
    <Typography variant='body2' color={colors.neutral[70]} sx={{ pt: '.25rem' }} noWrap>
      {children}
    </Typography>
  );
};

export const RequestsCompaniesList = ({ selectedRows }: { selectedRows: IKPIRequestDataModel[] | null }) => {
  const formatRespondents = FormatterService.get().getFormatterForId('userByEmail');
  const portcosById = useAtomValue(portcosByIdMapAtomJ);
  if (!selectedRows?.length) {
    return null;
  }

  return (
    <>
      {selectedRows?.map((row) => {
        const company = portcosById.get(row.companyId ?? -1);
        return (
          <CompanyPaperContainer key={row.id} elevation={1}>
            <CellContainer>
              <MUIAvatar
                nameParts={company?.name?.split(' ') ?? []}
                src={company?.logoUrl as string}
                size={'medium'}
              />
              <Typography variant={'body2'}>{company?.name}</Typography>
            </CellContainer>
            <RowTypography>{FMT.format('kpiTemplateName', row.templateUuid)}</RowTypography>
            <RowTypography>{upperFirst(row.frequency)}</RowTypography>
            <RowTypography>{formatRespondents(row.respondent)}</RowTypography>
          </CompanyPaperContainer>
        );
      })}
    </>
  );
};

export function UnsentKpiRequestsGrid() {
  const {
    state: { isLoading: isUpdatingOrDeleting },
  } = useLoadingBarState();

  const requestsNotSent: IKPIRequestDataModel[] | null =
    useAtomValue(filterKPIRequestsByStatusesJ([KPIRequestStatus.NotSent])) ?? null;
  const { columnDefs, defaultColDef } = useKPIRequestsNotSentColDefs();
  const { sendKPIRequestAction } = useKPIRequestsNotSentActions();

  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [selectedRows, setSelectedRows] = useState<IKPIRequestDataModel[] | null>(null);

  const [showSentModal, setShowSentModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useAtom(kpiRequestErrorModalOpenState);
  const failedRequestIds = useAtomValue(kpiRequestsNotSentState);
  const { exportParams } = useExtendedExportSettings(
    new Set(['companyId', 'templateUuid']),
    'kpi-requests-not-sent'
  );
  const portcosById = useAtomValue(portcosByIdMapAtomJ);

  const onGridReady = (params: { api: GridApi }) => {
    setGridApi(params.api);
  };

  const handleExport = useCallback(() => {
    gridApi?.exportDataAsExcel();
  }, [gridApi]);

  const selectedRequestCompanies = useMemo(() => {
    const selectedCompanies = new Map<number, ICompanyDataModel>();
    selectedRows?.forEach((row) => {
      const company = portcosById.get(row.companyId ?? -1);
      if (company && !selectedCompanies.has(company?.id)) {
        selectedCompanies.set(company.id, company);
      }
    });

    return selectedCompanies;
  }, [portcosById, selectedRows]);

  const handleSelectionChanged = useCallback(
    (e: SelectionChangedEvent) => {
      const selectedRows = e.api.getSelectedRows();
      setSelectedRows(selectedRows);
    },
    [setSelectedRows]
  );

  const handleOnConfirmSendAll = useCallback(async () => {
    if (!gridApi || !selectedRows) {
      return;
    }
    const selectedRowsIds = selectedRows.map((row) => row.id);

    const payload = {
      requestsIds: selectedRowsIds as number[],
      totalCompanies: selectedRequestCompanies.size,
    };

    await sendKPIRequestAction(payload);
    setShowSentModal(false);

    gridApi?.deselectAll();
    setSelectedRows(null);
  }, [gridApi, selectedRequestCompanies.size, selectedRows, sendKPIRequestAction]);

  const handleOnSendAll = useCallback(() => {
    setShowSentModal(true);
  }, []);

  const handleOnCancelAll = () => {
    gridApi?.deselectAll();
  };

  const getRowId = useCallback((params: GetRowIdParams<IKPIRequestDataModel>) => {
    return params.data.id.toString();
  }, []);

  const failedRequests = useMemo(() => {
    return requestsNotSent?.filter((request) => failedRequestIds.includes(request.id));
  }, [requestsNotSent, failedRequestIds]);

  return (
    <Stack height={'100%'}>
      {showErrorModal && (
        <RequestFailDialog
          open={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          requests={failedRequests ?? []}
          title='Error Sending Requests'
          message='Some requests were not sent due to errors.'
        />
      )}
      {showSentModal && (
        <RequestConfirmDialog
          open={showSentModal}
          onClose={() => setShowSentModal(false)}
          loading={isUpdatingOrDeleting}
          onConfirm={handleOnConfirmSendAll}
          requests={selectedRows ?? []}
          message={` Selected ${selectedRequestCompanies.size} companies:`}
          title='Confirm All Requests'
        />
      )}
      <div css={ExportButtonContainer}>
        <ExportGridButton onClick={handleExport} />
      </div>
      {!requestsNotSent?.length ? (
        <Box
          sx={{
            backgroundColor: 'white',
            width: '100%',
            p: '.75rem',
            borderRadius: '4px',
            borderLeft: '6px solid',
            borderColor: 'green',
          }}
        >
          All assigned requests are sent
        </Box>
      ) : (
        <FadeInGridWrapper>
          <ActionButtons selectedRows={selectedRows} actions={[handleOnSendAll, handleOnCancelAll]} />

          <AgTable
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            getRowId={getRowId}
            groupDefaultExpanded={-1}
            onGridReady={onGridReady}
            onSelectionChanged={handleSelectionChanged}
            rowData={requestsNotSent}
            rowGroupPanelShow='always'
            suppressMovableColumns={true}
            suppressRowDrag={true}
            defaultExcelExportParams={exportParams}
            defaultCsvExportParams={exportParams}
            rowSelection={multiRowSelectionDefs}
          />
        </FadeInGridWrapper>
      )}
    </Stack>
  );
}

interface ActionButtonsProps {
  selectedRows: IKPIRequestDataModel[] | null;
  actions: (() => void)[];
}

const ActionButtons = memo(function ActionButtons({ actions, selectedRows }: ActionButtonsProps) {
  const { colors } = useTheme();
  const {
    state: { isLoading },
  } = useLoadingBarState();
  const [handleOnSendAll, handleOnCancelAll] = actions;

  return (
    <ActionButtonWrapper>
      {selectedRows?.length ? (
        <ActionButtonStack direction='row'>
          <Button disabled={isLoading} variant='contained' color='secondary' onClick={handleOnSendAll}>
            Send Selected {selectedRows?.length ? `(${selectedRows.length})` : ''}
          </Button>
          <Button
            variant='outlined'
            color='secondary'
            sx={{
              backgroundColor: colors.primary[5],
            }}
            onClick={handleOnCancelAll}
          >
            Cancel
          </Button>
        </ActionButtonStack>
      ) : null}
    </ActionButtonWrapper>
  );
});
